import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { haptic } from '../../../shared/utils/haptics';

// Configuration du timer Pomodoro
export const POMODORO_PRESETS = {
  short: { work: 15, break: 3, label: 'Court (15 min)' },
  standard: { work: 25, break: 5, label: 'Standard (25 min)' },
  long: { work: 45, break: 10, label: 'Long (45 min)' },
  micro: { work: 2, break: 1, label: 'Juste 2 min' }
};

// Messages d'encouragement pendant le focus
export const FOCUS_MESSAGES = {
  start: [
    "C'est parti ! Tu peux le faire ! 💪",
    "Un pas à la fois, tu gères ! 🚀",
    "Focus activé ! Mode champion ! 🏆"
  ],
  halfway: [
    "Mi-parcours ! Continue, tu y es presque ! ⭐",
    "La moitié est faite ! Tu assures ! 🔥",
    "Keep going ! Tu es sur la bonne voie ! 💎"
  ],
  almost: [
    "Dernière ligne droite ! 🎯",
    "Plus que quelques minutes ! Tu y es ! ✨",
    "Finish strong ! 💪"
  ],
  complete: [
    "BRAVO ! Session terminée ! 🎉",
    "Tu l'as fait ! Quelle victoire ! 🏆",
    "Excellente session ! Tu es incroyable ! ⭐"
  ],
  break: [
    "Pause bien méritée ! 🌿",
    "Respire, tu l'as mérité ! 😌",
    "Petite pause pour recharger ! ☕"
  ]
};

// Hook principal pour le mode focus
export function useFocusMode() {
  const [focusState, setFocusState] = useLocalStorage('adhd-planner-focus', {
    sessionsCompleted: 0,
    totalFocusTime: 0,
    lastSessionDate: null
  });

  const [isActive, setIsActive] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [timerMode, setTimerMode] = useState('work');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [preset, setPreset] = useState('standard');
  const [currentMessage, setCurrentMessage] = useState('');

  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const startFocus = useCallback((task, selectedPreset = 'standard') => {
    const presetConfig = POMODORO_PRESETS[selectedPreset];
    const workTime = presetConfig.work * 60;

    setCurrentTask(task);
    setPreset(selectedPreset);
    setTimerMode('work');
    setTimeRemaining(workTime);
    setTotalTime(workTime);
    setIsActive(true);
    setIsPaused(false);
    startTimeRef.current = Date.now();

    // Haptic feedback for focus start
    haptic.focusStart();

    const startMessages = FOCUS_MESSAGES.start;
    setCurrentMessage(startMessages[Math.floor(Math.random() * startMessages.length)]);
  }, []);

  const stopFocus = useCallback((completed = false) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (completed && timerMode === 'work') {
      const timeSpent = totalTime - timeRemaining;

      setFocusState(prev => ({
        ...prev,
        sessionsCompleted: prev.sessionsCompleted + 1,
        totalFocusTime: prev.totalFocusTime + Math.round(timeSpent / 60),
        lastSessionDate: new Date().toISOString()
      }));

      // Haptic feedback for focus end
      haptic.focusEnd();
    }

    setIsActive(false);
    setCurrentTask(null);
    setTimeRemaining(0);
    setIsPaused(false);
    setCurrentMessage('');
  }, [timerMode, totalTime, timeRemaining, setFocusState]);

  const togglePause = useCallback(() => {
    haptic.tap();
    setIsPaused(prev => !prev);
  }, []);

  const extendSession = useCallback((extraMinutes = 5) => {
    haptic.tap();
    setTimeRemaining(prev => prev + extraMinutes * 60);
    setTotalTime(prev => prev + extraMinutes * 60);
  }, []);

  const startBreak = useCallback(() => {
    const presetConfig = POMODORO_PRESETS[preset];
    const breakTime = presetConfig.break * 60;

    setTimerMode('break');
    setTimeRemaining(breakTime);
    setTotalTime(breakTime);
    setIsPaused(false);

    haptic.success();

    const breakMessages = FOCUS_MESSAGES.break;
    setCurrentMessage(breakMessages[Math.floor(Math.random() * breakMessages.length)]);
  }, [preset]);

  useEffect(() => {
    if (isActive && !isPaused && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1;

          const progress = 1 - (newTime / totalTime);
          if (progress >= 0.5 && progress < 0.52 && timerMode === 'work') {
            const msgs = FOCUS_MESSAGES.halfway;
            setCurrentMessage(msgs[Math.floor(Math.random() * msgs.length)]);
          } else if (progress >= 0.85 && progress < 0.87 && timerMode === 'work') {
            const msgs = FOCUS_MESSAGES.almost;
            setCurrentMessage(msgs[Math.floor(Math.random() * msgs.length)]);
          }

          if (newTime <= 0) {
            clearInterval(intervalRef.current);

            if (timerMode === 'work') {
              const msgs = FOCUS_MESSAGES.complete;
              setCurrentMessage(msgs[Math.floor(Math.random() * msgs.length)]);

              // Haptic notification
              haptic.focusEnd();

              if (Notification.permission === 'granted') {
                new Notification('Session Focus terminée !', {
                  body: `Tu as complété ${POMODORO_PRESETS[preset].work} minutes de focus !`,
                  icon: '/favicon.ico'
                });
              }
            }

            return 0;
          }

          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isPaused, timerMode, totalTime, preset]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = totalTime > 0 ? ((totalTime - timeRemaining) / totalTime) * 100 : 0;

  return {
    isActive,
    currentTask,
    timerMode,
    timeRemaining,
    totalTime,
    isPaused,
    preset,
    progress,
    currentMessage,
    formattedTime: formatTime(timeRemaining),
    sessionsCompleted: focusState.sessionsCompleted,
    totalFocusTime: focusState.totalFocusTime,
    startFocus,
    stopFocus,
    togglePause,
    extendSession,
    startBreak,
    setPreset,
    POMODORO_PRESETS
  };
}

// Hook pour "Juste 2 minutes"
export function useJust2Minutes() {
  const [isRunning, setIsRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [taskId, setTaskId] = useState(null);

  const start = useCallback((task) => {
    haptic.focusStart();
    setTaskId(task.id);
    setTimeLeft(120);
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
    setTaskId(null);
    setTimeLeft(120);
  }, []);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timeLeft === 0) {
      haptic.success();
    }
  }, [isRunning, timeLeft]);

  return {
    isRunning,
    timeLeft,
    taskId,
    formattedTime: `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`,
    start,
    stop,
    isComplete: timeLeft === 0
  };
}

export default useFocusMode;
